import { StandardEvents } from '@shopify/events';

let instance = 0;
let cartRequest;
// A selection is explicit: a placeholder or disabled (sold-out) option can
// never enable submission. The existing product form owns cart requests/errors.
class RecommendationOptions extends HTMLElement {
  connectedCallback() {
    this.addEventListener('change', this.onChange);
    this.onChange();
  }
  disconnectedCallback() { this.removeEventListener('change', this.onChange); }
  onChange = () => {
    const select = this.querySelector('[data-sa-choice]');
    const option = select?.selectedOptions[0];
    const valid = Boolean(option?.value && !option.disabled);
    const input = this.querySelector('input[name="id"]');
    const button = this.querySelector('button[type="submit"]');
    if (!input || !button) return;
    input.value = valid ? option.value : '';
    input.disabled = !valid;
    button.disabled = !valid;
  };
}
if (!customElements.get('sa-recommendation-options')) {
  customElements.define('sa-recommendation-options', RecommendationOptions);
}
const readCart = (url) => {
  if (!cartRequest) {
    cartRequest = fetch(url, {credentials:'same-origin', cache:'no-store'})
      .then(response => { if (!response.ok) throw new Error('Cart unavailable'); return response.json(); })
      .finally(() => { cartRequest = undefined; });
  }
  return cartRequest;
};

export function selectRecommendationCards(cards, excluded, limit) {
  const seen = new Set(excluded.map(String));
  const selected = [];
  for (const card of cards) {
    const id = String(card.dataset.productId || '');
    if (!id || seen.has(id) || Number(card.dataset.price) <= 0) continue;
    seen.add(id);
    selected.push(card);
    if (selected.length >= limit) break;
  }
  return selected;
}

// Share the available slots across cart items without changing Shopify's
// relevance order within each item's recommendation list.
export function interleaveRecommendationGroups(groups) {
  const cards = [];
  const length = Math.max(0, ...groups.map(group => group.length));
  for (let index = 0; index < length; index++) {
    for (const group of groups) if (group[index]) cards.push(group[index]);
  }
  return cards;
}

class NativeRecommendations extends HTMLElement {
  connectedCallback() {
    this.instance = ++instance;
    this.version = 0;
    this.seeds = JSON.parse(this.dataset.seeds || '[]');
    this.excluded = JSON.parse(this.dataset.excluded || '[]');
    this.events = new AbortController();
    this.observer = new IntersectionObserver(entries => {
      if (entries.some(entry => entry.isIntersecting)) {
        this.observer.disconnect();
        this.started = true;
        this.load();
      }
    }, {rootMargin:'100px'});
    this.observer.observe(this);
    document.addEventListener(StandardEvents.cartLinesUpdate, this.onCartUpdate, {signal:this.events.signal});
  }

  disconnectedCallback() {
    ++this.version;
    this.observer?.disconnect();
    this.requests?.abort();
    this.events?.abort();
  }

  onCartUpdate = async (event) => {
    const version = ++this.version;
    this.requests?.abort();
    try {
      if (event.promise) await event.promise;
      const cart = await readCart(this.dataset.cartUrl);
      if (!this.isConnected || version !== this.version) return;
      this.excluded = [...new Set(cart.items.map(item => String(item.product_id)))];
      if (this.dataset.context !== 'product') this.seeds = this.excluded;
      this.hidden = false;
      // A previously empty panel is hidden and cannot intersect again.
      // Refresh on cart changes so adding a new item can revive it.
      this.started = true;
      this.load();
    } catch (error) {
      if (error.name !== 'AbortError') console.warn('Recommendation refresh unavailable');
    }
  };

  async candidates(productId, intent, signal) {
    const url = new URL(this.dataset.url, location.origin);
    url.searchParams.set('section_id','sa-recommendation-candidates');
    url.searchParams.set('product_id',productId);
    url.searchParams.set('intent',intent);
    url.searchParams.set('limit','10');
    const response = await fetch(url, {signal, credentials:'same-origin'});
    if (!response.ok) throw new Error('Recommendations unavailable');
    const html = new DOMParser().parseFromString(await response.text(),'text/html');
    const cards = [...html.querySelectorAll('[data-sa-recommendation-candidates] product-card')];
    cards.hasRecommendations = html.querySelector('[data-sa-recommendation-candidates]')?.dataset.hasRecommendations === 'true';
    return cards;
  }

  async load() {
    const version = ++this.version;
    this.requests?.abort();
    this.requests = new AbortController();
    const signal = this.requests.signal;
    const limit = Number(this.dataset.limit) || 3;
    const allSeeds = [...new Set(this.seeds.map(String))];
    const seeds = allSeeds.slice(0, this.dataset.context === 'product' ? 1 : 3);
    const excluded = [...this.excluded, ...allSeeds];
    try {
      let results = await Promise.allSettled(seeds.map(id => this.candidates(id,'complementary',signal)));
      // A basket with curated complements keeps its add-on intent even after
      // one is added. Uncurated baskets retain the existing related fallback.
      const hasCurated = results.some(result => result.status === 'fulfilled' && result.value.hasRecommendations);
      const relatedSeeds = hasCurated ? [] : seeds.filter((id,index) => results[index].status === 'fulfilled');
      let cards = interleaveRecommendationGroups(results.map(result => result.status === 'fulfilled' ? result.value : []));
      let chosen = selectRecommendationCards(cards,excluded,limit);
      if (chosen.length < limit && relatedSeeds.length && this.dataset.context !== 'product') {
        results = await Promise.allSettled(relatedSeeds.map(id => this.candidates(id,'related',signal)));
        cards = interleaveRecommendationGroups(results.map(result => result.status === 'fulfilled' ? result.value : []));
        // Complementary items stay first; only uncurated seeds get related items.
        chosen = selectRecommendationCards([...chosen, ...cards],excluded,limit);
      }
      if (!this.isConnected || signal.aborted || version !== this.version) return;
      for (const card of chosen) {
        const prefix = 'sa-rec-' + this.instance + '-' + card.dataset.productId + '-';
        for (const element of [card,...card.querySelectorAll('[id]')]) {
          if (!element.id) continue;
          const oldId = element.id;
          const newId = prefix + oldId;
          for (const label of card.querySelectorAll('[for]')) {
            if (label.getAttribute('for') === oldId) label.setAttribute('for',newId);
          }
          element.id = newId;
        }
      }
      this.querySelector('.sa-native-recs__list').replaceChildren(...chosen);
      this.querySelector('.sa-native-recs__heading').hidden = chosen.length === 0;
      this.hidden = chosen.length === 0;
    } catch (error) {
      if (error.name !== 'AbortError' && version === this.version) this.hidden = true;
    }
  }
}
if (!customElements.get('sa-native-recommendations')) {
  customElements.define('sa-native-recommendations', NativeRecommendations);
}
